import React, { useState, useEffect, useContext } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { Card, Row, Col, Button, Alert, Modal } from 'react-bootstrap';
import CuentasTreeView from './CuentasTreeView';
import CuentasTableView from './CuentasTableView';
import DetalleCuentaPanel from './DetalleCuentaPanel';
import AgregarCuentaModal from './AgregarCuentaModal';
import ImportarCuentasModal from './ImportarCuentasModal';
import EliminarCuentaModal from './EliminarCuentaModal';
import { ContabilidadLayout } from 'akasia-components/Contabilidad/common/ContabilidadLayout';
import { ContabilidadContext } from 'akasia-components/Contabilidad/common/ContabilidadContext';

const AdministrarCuentasContablesContent = () => {
  const [cuentasEmpresa, setCuentasEmpresa] = useState([]);
  const [catalogoSAT, setCatalogoSAT] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [tiposCuenta, setTiposCuenta] = useState([]);
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'table'
  
  const { token } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();
  const { selectedCompany, openCompanySelector } = useContext(ContabilidadContext);

  const [showAddModal, setShowAddModal] = useState(false);
  const [parentReadonly, setParentReadonly] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [accountsToDelete, setAccountsToDelete] = useState([]);

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("Información");
  const [infoModalMessage, setInfoModalMessage] = useState("");
  const [infoModalDetails, setInfoModalDetails] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const displayError = (message) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  };
  const [newAccount, setNewAccount] = useState({
    idCuentaContable: '',
    vCodigoCuenta: '',
    vNombreCuenta: '',
    idCuentaPadre: '',
    idCatCuentaContableSAT: '',
    iNivel: 1,
    iTipoNivelCuenta: 1,
    vNaturaleza: 'D',
    bIsActive: true
  });

  useEffect(() => {
    const fetchTiposCuenta = async () => {
      try {
        const response = await fetch('/api/contabilidad/libros/CuentasContables/obtenerTiposCuentaContable', {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        const data = await response.json();
        if (data.iResponseType === 1 && data.detalles) {
          setTiposCuenta(data.detalles);
        }
      } catch (err) {
        console.error("Error al cargar tipos de cuenta", err);
      }
    };
    
    const fetchCatalogoSAT = async () => {
      try {
        const response = await fetch('/api/contabilidad/libros/CuentasContables/ObtenerCatalogoSAT', {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        const data = await response.json();
        if (data.iResponseType === 1 && data.detalles) {
          setCatalogoSAT(data.detalles);
        }
      } catch (err) {
        console.error("Error al cargar SAT", err);
      }
    };

    if (token) {
      fetchTiposCuenta();
      fetchCatalogoSAT();
    }
  }, [token]);

  const loadCuentasEmpresa = async () => {
      if (!selectedCompany) {
          setCuentasEmpresa([]);
          return;
      }
      try {
        const response = await fetch(`/api/contabilidad/libros/CuentasContables/ObtenerCuentasContablesEmpresa?idDatoFiscal=${selectedCompany.idDatoFiscal}`, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        const data = await response.json();
        if (data.iResponseType === 1 && data.detalles) {
          setCuentasEmpresa(data.detalles);
          setSelectedAccount(prev => {
            if (prev) {
              const updated = data.detalles.find(c => c.idCuentaContable === prev.idCuentaContable);
              return updated || prev;
            }
            return prev;
          });
        } else {
          setCuentasEmpresa([]);
        }
      } catch (err) {
        console.error("Error al cargar cuentas de empresa", err);
      }
  };

  useEffect(() => {
    loadCuentasEmpresa();
  }, [selectedCompany, token]);

  const handleAddChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAccount(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOpenAddModal = () => {
    if (!selectedCompany) {
        showErrorToast("Por favor selecciona una empresa primero.");
        openCompanySelector();
        return;
    }
    setNewAccount({
      idCuentaContable: '', vCodigoCuenta: '', vNombreCuenta: '', idCuentaPadre: '', idCatCuentaContableSAT: '',
      iNivel: 1, iTipoNivelCuenta: 1, vNaturaleza: 'D', bIsActive: true
    });
    setParentReadonly(false);
    setShowAddModal(true);
  };

  const handleAddChild = (parentNode) => {
    if (parentNode.iTipoNivelCuenta?.toString() === '3') {
      showErrorToast("No se puede agregar una cuenta hija a una cuenta de detalle.");
      return;
    }

    setNewAccount({
      idCuentaContable: '', 
      vCodigoCuenta: '', 
      vNombreCuenta: '', 
      idCuentaPadre: parentNode.idCuentaContable, 
      idCatCuentaContableSAT: '',
      iNivel: parseInt(parentNode.iNivel || 1, 10) + 1, 
      iTipoNivelCuenta: 1, 
      vNaturaleza: parentNode.vNaturaleza || 'D', 
      bIsActive: true
    });
    setParentReadonly(true);
    setShowAddModal(true);
  };

  const handleEditAccount = (account) => {
    setNewAccount({
      ...account
    });
    setParentReadonly(false);
    setShowAddModal(true);
  };

  const handleSaveNewAccount = async () => {
    if (newAccount.idCuentaPadre) {
      const parentNode = cuentasEmpresa.find(c => c.idCuentaContable.toString() === newAccount.idCuentaPadre.toString());
      if (parentNode && parentNode.iTipoNivelCuenta?.toString() === '3') {
        showErrorToast("La cuenta padre seleccionada es de detalle. Solo se pueden agregar hijas a cuentas agrupadoras.");
        return;
      }
    }

    if (!newAccount.idCatCuentaContableSAT) {
        showErrorToast("Debes seleccionar una cuenta agrupadora del SAT.");
        return;
    }

    try {
      const payload = {
          ...newAccount,
          idDatoFiscal: selectedCompany.idDatoFiscal
      };

      const response = await fetch("/api/contabilidad/libros/CuentasContables/insertarCuentaContable", {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.iResponseType === 1) {
        showSuccessToast("Cuenta guardada exitosamente");
        setShowAddModal(false);
        loadCuentasEmpresa(); 
      } else {
        displayError(data.vMessage || "Error al guardar la cuenta");
      }
    } catch (error) {
      displayError("Error en el servidor");
    }
  };

  const handleSaveEditedAccount = async (updatedAccount, isDirectSave = false) => {
    // If it's a direct save from the table, we call the API directly
    if (isDirectSave) {
        try {
            const payload = {
                ...updatedAccount,
                idDatoFiscal: selectedCompany.idDatoFiscal
            };
            const response = await fetch("/api/contabilidad/libros/CuentasContables/insertarCuentaContable", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.iResponseType === 1) {
                showSuccessToast("Cuenta actualizada exitosamente");
                loadCuentasEmpresa(); 
            } else {
                displayError(data.vMessage || "Error al actualizar la cuenta");
            }
        } catch (error) {
            displayError("Error en el servidor");
        }
    }
  };

  const promptDeleteAccount = (accounts) => {
    if (Array.isArray(accounts)) {
      setAccountsToDelete(accounts);
    } else {
      setAccountsToDelete([accounts]);
    }
    setShowDeleteModal(true);
  };

  const executeDeleteAccount = async (accounts) => {
    setIsDeleting(true);
    try {
      const idsStr = accounts.map(a => a.idCuentaContable).join(',');
      const payload = {
          idsCuentasContables: idsStr,
          idDatoFiscal: selectedCompany.idDatoFiscal
      };

      const response = await fetch("/api/contabilidad/libros/CuentasContables/EliminarCuentaContable", {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.iResponseType === 1) {
        if (data.detalles && data.detalles.length > 0) {
            const hasFailures = data.detalles.some(d => d.Status === 'FALLA');
            const isMultiple = data.detalles.length > 1;
            
            if (hasFailures || isMultiple) {
              setInfoModalTitle("Resultado de Eliminación");
              setInfoModalDetails(data.detalles);
              setInfoModalMessage("");
              setShowInfoModal(true);
            } else {
              showSuccessToast("Cuenta eliminada exitosamente");
            }
        } else if (data.vMessage && data.vMessage.includes("No se pudieron eliminar")) {
          setInfoModalTitle("Resultado de Eliminación");
          setInfoModalMessage(data.vMessage);
          setInfoModalDetails(null);
          setShowInfoModal(true);
        } else {
          showSuccessToast(data.vMessage || "Operación completada");
        }
        
        if (selectedAccount && accounts.some(a => a.idCuentaContable === selectedAccount.idCuentaContable)) {
            setSelectedAccount(null);
        }
        setShowDeleteModal(false);
        loadCuentasEmpresa(); 
      } else {
        displayError(data.vMessage || "Error al procesar la eliminación");
      }
    } catch (error) {
      displayError("Error en el servidor al intentar eliminar");
    } finally {
      setIsDeleting(false);
    }
  };

  const buildTree = (flatData) => {
    const map = new Map();
    const roots = [];

    flatData.forEach(node => {
      map.set(node.idCuentaContable, { ...node, idCatCuentaContable: node.idCuentaContable, children: [] });
    });

    flatData.forEach(node => {
      const parentId = node.idCuentaPadre;
      if (parentId) {
        const parent = map.get(parentId);
        if (parent) {
          parent.children.push(map.get(node.idCuentaContable));
        } else {
          roots.push(map.get(node.idCuentaContable));
        }
      } else {
        roots.push(map.get(node.idCuentaContable));
      }
    });

    return roots;
  };

  const treeData = buildTree(cuentasEmpresa);

  return (
    <Card className="mb-3">
      <Card.Header className="bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-primary fw-bold">
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            Administración de Cuentas Contables
          </h5>
          <div>
            <Button 
                variant={viewMode === 'tree' ? "primary" : "outline-primary"} 
                size="sm" 
                className="me-2" 
                onClick={() => setViewMode('tree')}
            >
                Vista Árbol
            </Button>
            <Button 
                variant={viewMode === 'table' ? "primary" : "outline-primary"} 
                size="sm" 
                className="me-4" 
                onClick={() => setViewMode('table')}
            >
                Vista Tabla
            </Button>
            
            <Button variant="success" size="sm" className="me-2" onClick={handleOpenAddModal} disabled={!selectedCompany}>
              + Agregar Cuenta
            </Button>
            <Button variant="secondary" size="sm" className="me-3" onClick={() => setShowImportModal(true)} disabled={!selectedCompany}>
              Importar
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {!selectedCompany && (
            <Alert variant="warning">
                Selecciona una empresa para administrar sus cuentas.
            </Alert>
        )}
        
        {selectedCompany && viewMode === 'tree' && (
            <Row>
            {/* Panel Izquierdo: Árbol de Cuentas */}
            <Col md={7} lg={8}>
                <h6 className="mb-3">Estructura Propia</h6>
                <CuentasTreeView 
                data={treeData} 
                onSelectAccount={setSelectedAccount} 
                selectedAccountId={selectedAccount?.idCuentaContable} 
                onAddChild={handleAddChild}
                onDelete={promptDeleteAccount}
                />
            </Col>
            
            {/* Panel Derecho: Detalles de la cuenta seleccionada */}
            <Col md={5} lg={4}>
            <DetalleCuentaPanel 
              selectedAccount={selectedAccount} 
              onEdit={handleEditAccount}
              onDelete={promptDeleteAccount}
            />
          </Col>
            </Row>
        )}

        {selectedCompany && viewMode === 'table' && (
            <Row>
              <Col md={12}>
                <CuentasTableView 
                    data={cuentasEmpresa} 
                    catalogoSAT={catalogoSAT}
                    tiposCuenta={tiposCuenta}
                    onEditAccount={handleSaveEditedAccount}
                    onDeleteAccount={promptDeleteAccount}
                />
              </Col>
            </Row>
        )}
      </Card.Body>

      {/* Modales separados en componentes */}
      <AgregarCuentaModal 
        show={showAddModal} 
        onHide={() => setShowAddModal(false)} 
        newAccount={newAccount} 
        onChange={handleAddChange} 
        onSave={handleSaveNewAccount} 
        cuentasData={cuentasEmpresa}
        catalogoSAT={catalogoSAT}
        parentReadonly={parentReadonly}
        tiposCuenta={tiposCuenta}
      />

        <ImportarCuentasModal 
            show={showImportModal} 
            onHide={() => setShowImportModal(false)}
            onImportSuccess={() => {
                setShowImportModal(false);
                loadCuentasEmpresa();
            }}
            idDatoFiscal={selectedCompany?.idDatoFiscal}
        />
        
        <EliminarCuentaModal 
            show={showDeleteModal} 
            onHide={() => setShowDeleteModal(false)}
            onConfirm={executeDeleteAccount}
            accounts={accountsToDelete}
            isDeleting={isDeleting}
        />
        
        <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>Error de Validación</Modal.Title>
          </Modal.Header>
          <Modal.Body>{errorMessage}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-light border-0">
            <Modal.Title>{infoModalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {infoModalDetails ? (
              <Row>
                <Col md={6}>
                  <h6 className="text-success border-bottom pb-2 mb-3">✅ Cuentas Eliminadas</h6>
                  {infoModalDetails.filter(d => d.Status === 'EXITO').length === 0 ? (
                    <p className="text-muted small">No se eliminó ninguna cuenta.</p>
                  ) : (
                    <ul className="text-muted small ps-3">
                      {infoModalDetails.filter(d => d.Status === 'EXITO').map(d => (
                        <li key={d.idCuenta}><strong>{d.NombreCuenta}</strong></li>
                      ))}
                    </ul>
                  )}
                </Col>
                <Col md={6}>
                  <h6 className="text-danger border-bottom pb-2 mb-3">❌ Cuentas No Eliminadas</h6>
                  {infoModalDetails.filter(d => d.Status === 'FALLA').length === 0 ? (
                    <p className="text-muted small">No hubo errores.</p>
                  ) : (
                    <ul className="text-muted small ps-3">
                      {infoModalDetails.filter(d => d.Status === 'FALLA').map(d => (
                        <li key={d.idCuenta} className="mb-2">
                          <strong>{d.NombreCuenta}</strong><br/>
                          <span className="text-danger">{d.Motivo}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Col>
              </Row>
            ) : (
              <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{infoModalMessage}</p>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="secondary" onClick={() => setShowInfoModal(false)}>
              Entendido
            </Button>
          </Modal.Footer>
        </Modal>
    </Card>
  );
};

const AdministrarCuentasContables = () => {
  return (
    <ContabilidadLayout>
      <AdministrarCuentasContablesContent />
    </ContabilidadLayout>
  );
};

export default AdministrarCuentasContables;
